import { useState, useCallback } from 'react'
import { messageAPI } from '../../../services/api'
import { ConversationSummary, UserBasic } from '../../../types'

interface ProjectConversation {
  project_id: string
  project_name: string
  unread_count: number
  conversations: ConversationSummary[]
}

interface UseConversationsReturn {
  conversations: ConversationSummary[]
  allProjectConversations: ProjectConversation[]
  adminUsers: UserBasic[]
  loading: boolean
  loadingAdmins: boolean
  error: string | null
  loadConversations: (projectId: string) => Promise<void>
  loadAllProjectConversations: () => Promise<void>
  loadAdminUsers: (projectId: string) => Promise<void>
  markConversationAsRead: (projectId: string, partnerId: string) => Promise<void>
  clearError: () => void
}

export function useConversations(): UseConversationsReturn {
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [allProjectConversations, setAllProjectConversations] = useState<ProjectConversation[]>([])
  const [adminUsers, setAdminUsers] = useState<UserBasic[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingAdmins, setLoadingAdmins] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const loadConversations = useCallback(async (projectId: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await messageAPI.getConversations({ projectId, scope: 'project' })
      setConversations(response.data)
    } catch (error) {
      console.error('Error loading conversations:', error)
      setError('Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadAllProjectConversations = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await messageAPI.getConversations({ scope: 'all' })
      const conversations: ProjectConversation[] = response.data.map((project: any) => ({
        project_id: project.project_id,
        project_name: project.project_name,
        unread_count: project.unread_count,
        conversations: project.conversations,
      }))
      setAllProjectConversations(conversations)
    } catch (error) {
      console.error('Error loading all project conversations:', error)
      setError('Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadAdminUsers = useCallback(async (projectId: string) => {
    try {
      setLoadingAdmins(true)
      setError(null)
      const response = await messageAPI.getAdminUsers(projectId)
      setAdminUsers(response.data)
    } catch (error) {
      console.error('Error loading admin users:', error)
      setError('Failed to load admin users')
    } finally {
      setLoadingAdmins(false)
    }
  }, [])

  const markConversationAsRead = useCallback(async (projectId: string, partnerId: string) => {
    try {
      await messageAPI.markConversationAsRead(projectId, partnerId)
    } catch (error) {
      console.error('Error marking conversation as read:', error)
      // Silent fail for marking as read
    }
  }, [])

  return {
    conversations,
    allProjectConversations,
    adminUsers,
    loading,
    loadingAdmins,
    error,
    loadConversations,
    loadAllProjectConversations,
    loadAdminUsers,
    markConversationAsRead,
    clearError,
  }
}
